import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useGlobalSettings } from '../contexts/GlobalSettingsContext';
import supabase from '../supabase/supabase';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Logo from '../components/Logo';
import LanguageSwitcher from '../components/LanguageSwitcher';

const { FiSearch, FiMap, FiCheck, FiArrowRight, FiStar, FiMapPin } = FiIcons;

const LandingPage = () => {
  const { t } = useLanguage();
  const { settings } = useGlobalSettings();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [promotedVenues, setPromotedVenues] = useState([]);

  useEffect(() => {
    fetchFeatured();
  }, [settings.promotedAdsCount]);

  const fetchFeatured = async () => {
    const { data } = await supabase
      .from('venues_1780591882954')
      .select('*')
      .eq('is_promoted', true);
    
    if (data) {
      const shuffled = [...data].sort(() => 0.5 - Math.random());
      setPromotedVenues(shuffled.slice(0, settings.promotedAdsCount));
    }
  };

  const handleSearch = (e) => {
    e.stopPropagation();
    navigate(`/search?q=${encodeURIComponent(searchQuery || 'Iași')}`);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col overflow-x-hidden">
      <header className="fixed w-full z-50 bg-white/90 backdrop-blur-md shadow-sm border-b h-16 flex items-center px-4 lg:px-16">
        <div className="w-full flex justify-between items-center">
          <div onClick={() => navigate('/')} className="cursor-pointer">
            <Logo />
          </div>
          <div className="flex items-center space-x-6">
            <LanguageSwitcher />
            <Link to="/login" className="text-gray-600 font-medium text-sm">{t.nav.login}</Link>
            <Link to="/signup" className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-sm transition-all hover:bg-blue-700">
              {t.nav.signup}
            </Link>
          </div>
        </div>
      </header>

      <div className="pt-16 flex-1">
        {/* PROMOTED SECTION */}
        {promotedVenues.length > 0 && (
          <section className="bg-gradient-to-b from-gray-50 to-white py-12 px-4 lg:px-16 border-b">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <SafeIcon icon={FiStar} className="text-yellow-500" /> 
                    Cazare Premium în Iași
                  </h2>
                  <p className="text-gray-500 text-sm">Apartamente și vile selectate pentru confortul tău</p>
                </div>
                <Link to="/search" className="text-blue-600 font-bold text-sm hover:underline">Vezi toate ofertele</Link>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {promotedVenues.map((venue) => (
                  <motion.div 
                    key={venue.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ y: -5 }}
                    className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/book/${venue.owner_id}/${venue.id}`)}
                  >
                    <div className="relative h-48">
                      <img src={venue.photos?.[0]} className="w-full h-full object-cover" alt={venue.name} />
                      <div className="absolute top-3 right-3 bg-white/90 backdrop-blur px-2 py-1 rounded-lg text-xs font-bold text-blue-700 shadow-sm">
                        RECOMANDAT
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-gray-900 truncate">{venue.name}</h3>
                      <p className="text-xs text-gray-500 flex items-center mt-1">
                        <SafeIcon icon={FiMapPin} className="mr-1" /> {venue.address}
                      </p>
                      <div className="mt-4 flex justify-between items-center">
                        <span className="text-lg font-extrabold text-blue-600">€{venue.price_per_night}<span className="text-xs text-gray-400 font-normal">/noapte</span></span>
                        <div className="flex gap-1">
                          {venue.facilities?.slice(0, 2).map((f, i) => (
                            <span key={i} className="bg-gray-50 text-[10px] px-2 py-1 rounded text-gray-600 uppercase font-bold">{f}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* HERO SECTION */}
        <div className="flex flex-col lg:flex-row min-h-[70vh]">
          <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-16 bg-white border-r">
            <h1 className="text-4xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-6">
              Găsește cazarea perfectă în <span className="text-blue-600">Iași</span>
            </h1>
            <p className="text-lg text-gray-500 mb-8 max-w-lg">
              {t.landing.guestSub}
            </p>
            
            <div className="bg-white p-2 rounded-2xl shadow-2xl border flex max-w-md">
              <input 
                type="text" 
                placeholder={t.landing.searchPlaceholder}
                className="flex-1 px-4 outline-none text-sm"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              <button 
                onClick={handleSearch}
                className="bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-all"
              >
                Caută
              </button>
            </div>
          </div>

          <div className="flex-1 bg-gray-50 flex flex-col justify-center px-6 py-12 lg:px-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t.landing.ownerTitle}</h2>
            <p className="text-gray-500 mb-8">{t.landing.ownerSub}</p>
            <Link 
              to="/signup" 
              className="inline-flex items-center bg-gray-900 text-white px-8 py-4 rounded-xl font-bold hover:bg-black transition-all w-fit"
            >
              {t.landing.ownerButton} <SafeIcon icon={FiArrowRight} className="ml-2" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;